import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast, Sheet, SheetContent, SheetTitle, Button, Alert, AlertDescription, AlertTitle } from "@ndd/ui";
import { Badge } from "@/components/badge";
import { X, RefreshCw, Loader2, Trash2, ArrowUpRight } from "lucide-react";
import { DeleteSessionDialog } from "@/components/v2/delete-session-dialog";
import { useSessionAnalysis } from "@/lib/use-session-analysis";
import { AlwaysListScorecard } from "@/components/scorecard/always-list-scorecard";
import { CounselingSkillsScorecard } from "@/components/scorecard/counseling-skills-scorecard";
import { SessionActionsSection } from "@/components/narrative/session-actions-section";
import { MissedOpportunitiesSection } from "@/components/narrative/missed-opportunities-section";
import { ThingsDoneWellSection } from "@/components/narrative/things-done-well-section";
import { KeyImprovementsSection } from "@/components/narrative/key-improvements-section";
import { RedFlagsSection } from "@/components/narrative/red-flags-section";
import { SectionErrorFallback } from "@/components/narrative/section-error-fallback";
import { EditableSessionTitle } from "@/components/v2/editable-session-title";
import { ThinkingPanel } from "@/components/v2/thinking-panel";
import { authenticatedApi } from "@/lib/api";
import type { AnalysisResponse, SessionType } from "../../../../shared/analysis-types";
import { formatLocalDate } from "../../../../shared/local-date";
import type { SessionRowData } from "./session-row";

type AnalysisStatus = AnalysisResponse["status"];

function toSessionType(raw: "initial" | "follow_up" | null): SessionType {
  if (raw === "follow_up") return "follow-up";
  return raw ?? "initial";
}

function CloseButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className={`shrink-0 text-sand-400 hover:text-foreground ${className ?? ""}`}
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </Button>
  );
}

function DeleteButton({
  sessionId,
  onDeleted,
  className,
}: {
  sessionId: string;
  onDeleted: () => void;
  className?: string;
}) {
  return (
    <DeleteSessionDialog
      sessionId={sessionId}
      onDeleted={onDeleted}
      trigger={
        <Button
          variant="ghost"
          size="icon"
          className={`shrink-0 text-sand-400 hover:text-error ${className ?? ""}`}
        >
          <Trash2 className="h-4 w-4" />
          <span className="sr-only">Delete session</span>
        </Button>
      }
    />
  );
}

interface ScorecardPanelProps {
  rdId: string;
  rdSlug: string;
  session: SessionRowData;
  onClose: () => void;
  /**
   * Optional actions row rendered under the title block (the comp's
   * flyover-actions) — SourceFlyover slots "Ask about this" here.
   */
  headerActions?: React.ReactNode;
}

/**
 * Inner panel content. Keyed by session.id in the parent so React cleanly
 * remounts when the selected session changes — no render-phase state resets.
 * Exported for reuse by SourceFlyover, the workspace's non-modal panel.
 */
export function ScorecardPanelContent({ rdId, rdSlug, session, onClose, headerActions }: ScorecardPanelProps) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { data, loading, restart } = useSessionAnalysis(session.id);

  const [hasRetried, setHasRetried] = useState(false);
  const effectiveStatus: AnalysisStatus = hasRetried ? "pending" : session.status;
  const [retrying, setRetrying] = useState(false);

  const handleDeleted = useCallback(() => {
    toast.success("Session deleted");
    onClose();
    void queryClient.invalidateQueries({ queryKey: ["rd-sessions", rdId] });
    void queryClient.invalidateQueries({ queryKey: ["rd", rdId] });
  }, [onClose, queryClient, rdId]);

  const canDelete =
    effectiveStatus !== "pending" && effectiveStatus !== "analyzing";

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await authenticatedApi(getToken, `/api/sessions/${session.id}/retry`, {
        method: "POST",
      });
      setHasRetried(true);
      restart();
    } catch {
      // parity with legacy: a failed retry keeps the error state visible
    } finally {
      setRetrying(false);
    }
  }, [getToken, session.id, restart]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrolledPastSections, setScrolledPastSections] = useState<string[]>([]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    function handleScroll() {
      const scrollTop = container!.scrollTop;
      setIsScrolled(scrollTop > 80);

      const sectionEls = container!.querySelectorAll<HTMLElement>("[data-section-name]");
      const passed: string[] = [];
      sectionEls.forEach((el) => {
        if (el.offsetTop + el.offsetHeight < scrollTop + 60) {
          const name = el.getAttribute("data-section-name");
          if (name) passed.push(name);
        }
      });
      setScrolledPastSections(passed);
    }

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [data]);

  const sessionTitle = session.clientName || "Untitled Session";
  const sessionDate = formatLocalDate(session.sessionDate);
  const sessionType = toSessionType(session.sessionType);
  const isError = data?.status === "error" || (effectiveStatus === "error" && !data);
  const isAnalyzing = data?.status === "pending" || data?.status === "analyzing";

  return (
    <>
      {/* Header — full or compact based on scroll */}
      <div className="shrink-0 border-b border-sand-200 bg-card">
        {isScrolled ? (
          <div className="flex items-center justify-between px-8 py-3">
            <p className="text-xs text-sand-400 truncate">
              <span className="font-bold uppercase tracking-wider text-sand-500">Scorecard</span>
              <span className="mx-1.5">/</span>
              <span>{sessionTitle}</span>
              {sessionDate && (
                <>
                  <span className="mx-1"> — </span>
                  <span>{sessionDate}</span>
                </>
              )}
              {sessionType && (
                <>
                  <span className="mx-1.5">/</span>
                  <span className="capitalize">{sessionType}</span>
                </>
              )}
            </p>
            <div className="flex items-center gap-1 shrink-0">
              {canDelete && (
                <DeleteButton
                  sessionId={session.id}
                  onDeleted={handleDeleted}
                />
              )}
              <CloseButton onClick={onClose} />
            </div>
          </div>
        ) : (
          <div className="px-8 py-5">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold tracking-wider uppercase text-sand-500">
                  Session Scorecard
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <h2 className="text-2xl font-bold font-sans text-foreground flex items-baseline gap-2 min-w-0">
                    <EditableSessionTitle
                      sessionId={session.id}
                      initialClientName={session.clientName}
                      fallback="Untitled Session"
                      className="text-2xl font-bold font-sans text-foreground truncate"
                    />
                    {sessionDate && (
                      <span className="text-sand-400 font-normal shrink-0">— {sessionDate}</span>
                    )}
                  </h2>
                  {session.sessionType === "initial" ? (
                    <Badge variant="category">Initial</Badge>
                  ) : session.sessionType === "follow_up" ? (
                    <Badge variant="category">Follow-up</Badge>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 -mt-1 -mr-2">
                <Link
                  to="/rds/$rdId/sessions/$sessionId"
                  params={{ rdId: rdSlug, sessionId: session.shortId }}
                  className="text-xs text-sand-500 hover:text-foreground flex items-center gap-1"
                >
                  View full scorecard
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
                {canDelete && (
                  <DeleteButton
                    sessionId={session.id}
                    onDeleted={handleDeleted}
                  />
                )}
                <CloseButton onClick={onClose} />
              </div>
            </div>
            {headerActions && (
              <div className="mt-3 flex items-center gap-2">{headerActions}</div>
            )}
          </div>
        )}

        {isScrolled && scrolledPastSections.length > 0 && (
          <div className="text-xs text-sand-400 py-2 px-8 border-t border-sand-200">
            Scrolled past {scrolledPastSections.join(" · ")}
          </div>
        )}
      </div>

      {/* Panel body */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-8 py-6"
      >
        {loading && !data ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="h-6 w-6 text-sand-400 animate-spin" />
            <p className="text-sm text-sand-400">Loading scorecard...</p>
          </div>
        ) : isError ? (
          <div className="flex flex-col gap-4 py-8">
            <Alert variant="danger" className="rounded-xl">
              <AlertTitle>Analysis Error</AlertTitle>
              <AlertDescription>
                {data?.errorMessage ?? session.errorMessage ?? "An error occurred during analysis."}
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              onClick={() => void handleRetry()}
              disabled={retrying}
              className="self-start text-error border-error hover:bg-error-light hover:text-error"
            >
              <RefreshCw className={retrying ? "animate-spin" : ""} />
              Retry
            </Button>
          </div>
        ) : data ? (
          <div className="flex flex-col gap-6">
            {isAnalyzing && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-sky-100/50 border border-sky-100">
                <Loader2 className="h-4 w-4 text-sky-500 animate-spin" />
                <span className="text-sm text-sky-500">
                  Analysis in progress...
                </span>
              </div>
            )}

            <ThinkingPanel
              thinking={data.thinking}
              status={data.status}
              firstSectionReady={Object.keys(data.sections).length > 0}
            />

            {data.sections.session_overview && (
              <div data-section-name="Session Overview">
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
                  Session Overview
                </h3>
                {"error" in data.sections.session_overview ? (
                  <div className="mt-4">
                    <SectionErrorFallback sectionName="session_overview" />
                  </div>
                ) : (
                  <p className="mt-3 text-sm leading-relaxed text-foreground">
                    {data.sections.session_overview.text}
                  </p>
                )}
              </div>
            )}

            {data.sections.session_actions && (
              <div data-section-name="Session Actions">
                <SessionActionsSection data={data.sections.session_actions} />
              </div>
            )}

            {data.sections.always_list_scorecard && (
              <div data-section-name="Always List">
                <AlwaysListScorecard
                  data={data.sections.always_list_scorecard}
                  sessionType={sessionType}
                  promptVersion={session.promptVersion}
                />
              </div>
            )}

            {data.sections.counseling_skills_scorecard && (
              <div data-section-name="Counseling Skills">
                <CounselingSkillsScorecard
                  data={data.sections.counseling_skills_scorecard}
                  promptVersion={session.promptVersion}
                />
              </div>
            )}

            {data.sections.missed_opportunities && (
              <div data-section-name="Missed Opportunities">
                <MissedOpportunitiesSection data={data.sections.missed_opportunities} />
              </div>
            )}

            {data.sections.things_done_well && (
              <div data-section-name="Things Done Well">
                <ThingsDoneWellSection data={data.sections.things_done_well} />
              </div>
            )}

            {data.sections.key_improvements && (
              <div data-section-name="Key Improvements">
                <KeyImprovementsSection data={data.sections.key_improvements} />
              </div>
            )}

            {data.sections.red_flags && (
              <div data-section-name="Red Flags">
                <RedFlagsSection data={data.sections.red_flags} />
              </div>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}

interface ScorecardSlideoutProps {
  rdId: string;
  rdSlug: string;
  session: SessionRowData | null;
  open: boolean;
  onClose: () => void;
}

export function ScorecardPanel({ rdId, rdSlug, session, open, onClose }: ScorecardSlideoutProps) {
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="w-[720px] sm:max-w-[720px] p-0 shadow-[-10px_0_24px_rgba(40,3,15,0.1)] flex flex-col [&>button]:hidden"
      >
        <SheetTitle className="sr-only">Session Scorecard</SheetTitle>
        {session && (
          <ScorecardPanelContent
            key={session.id}
            rdId={rdId}
            rdSlug={rdSlug}
            session={session}
            onClose={onClose}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
